import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Database,
  Shield,
  Layers,
  Activity,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ArrowUpRight,
  Radar,
  Zap,
  Settings,
  Globe,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SubscriptionFilter from "@/components/SubscriptionFilter";

export default function DashboardPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [driftStatus, setDriftStatus] = useState(null);
  const [driftAlerts, setDriftAlerts] = useState([]);
  const [checkingDrift, setCheckingDrift] = useState(false);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const params = subscriptionId
        ? `?subscription_id=${subscriptionId}`
        : "";
      const [statsRes, driftRes, alertsRes] = await Promise.all([
        api().get(`/dashboard/stats${params}`),
        api().get("/drift/status"),
        api().get("/drift/alerts"),
      ]);
      setStats(statsRes.data);
      setDriftStatus(driftRes.data);
      setDriftAlerts(alertsRes.data.filter((a) => a.status === "new").slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoading(false);
    }
  }, [api, subscriptionId]);

  const triggerDriftCheck = async () => {
    setCheckingDrift(true);
    try {
      const res = await api().post("/drift/check-now");
      const driftFound = res.data.results?.some((r) => r.status === "drift_detected");
      if (driftFound) {
        toast.warning("Drift detected! Check alerts below.");
      } else {
        toast.success("All credentials healthy — no drift detected.");
      }
      fetchStats();
    } catch (err) {
      toast.error("Drift check failed");
    } finally {
      setCheckingDrift(false);
    }
  };

  const acknowledgeDriftAlert = async (alertId) => {
    try {
      await api().put(`/drift/alerts/${alertId}/acknowledge`);
      setDriftAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success("Alert acknowledged");
    } catch (err) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      const res = await api().post("/scan/trigger");
      const results = res.data.results || [];
      const successes = results.filter((r) => r.status === "success");
      const failures = results.filter((r) => r.status === "error");

      if (failures.length === 0 && successes.length > 0) {
        toast.success(
          `Scan complete! ${successes.length} credential(s) scanned successfully.`
        );
      } else if (failures.length > 0 && successes.length > 0) {
        toast.warning(
          `Partial scan: ${successes.length} succeeded, ${failures.length} failed.`
        );
        failures.forEach((f) => {
          toast.error(`${f.credential}: ${f.error}`, { duration: 8000 });
        });
      } else if (failures.length > 0 && successes.length === 0) {
        failures.forEach((f) => {
          toast.error(`${f.credential}: ${f.error}`, { duration: 8000 });
        });
      } else {
        toast.info("Scan completed with no results.");
      }
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48 bg-[var(--n-border-2)]" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-[var(--n-card)]" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Resources",
      value: stats?.total_resources || 0,
      icon: Database,
      accent: "text-[var(--hig-accent)]",
      bg: "bg-[var(--hig-accent)]/10",
    },
    {
      label: "Critical / Prod",
      value: stats?.critical_resources || 0,
      icon: Shield,
      accent: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-500/10",
    },
    {
      label: "Resource Groups",
      value: stats?.resource_groups || 0,
      icon: Layers,
      accent: "text-violet-600 dark:text-violet-400",
      bg: "bg-violet-500/10",
    },
    {
      label: "Discovery Events",
      value: stats?.total_events || 0,
      icon: Activity,
      accent: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-500/10",
    },
  ];

  const eventTypeColors = {
    created: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    modified: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    deleted: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  };

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-heading">
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor your Azure infrastructure in real-time
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SubscriptionFilter
            value={subscriptionId}
            onChange={setSubscriptionId}
          />
          <Button
            onClick={triggerScan}
            disabled={scanning}
            data-testid="trigger-scan-button"
            className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
          >
          {scanning ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...
            </>
          ) : (
            <>
              <Radar className="w-4 h-4 mr-2" /> Scan Now
            </>
          )}
        </Button>
        </div>
      </div>

      {stats?.scanner_status && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span
                className={`w-2 h-2 rounded-full ${stats.scanner_status.running ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`}
              />
              Scanner {stats.scanner_status.running ? "active" : "inactive"}
            </span>
            {stats.scanner_status.last_scan && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last: {new Date(stats.scanner_status.last_scan).toLocaleString()}
              </span>
            )}
            {stats.scanner_status.scan_count > 0 && (
              <span>{stats.scanner_status.scan_count} scans completed</span>
            )}
          </div>
          {stats.scanner_status.recent_errors?.length > 0 && (
            <div className="space-y-2" data-testid="scanner-errors">
              {stats.scanner_status.recent_errors.map((err, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {err.credential}:
                    </span>{" "}
                    <span className="text-red-600 dark:text-red-400/80">{err.error}</span>
                    <span className="text-muted-foreground block mt-0.5">
                      {new Date(err.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Drift Monitoring */}
      <div className="glass-card rounded-xl p-4" data-testid="drift-monitor">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {driftStatus?.alerts?.critical > 0 ? (
              <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            )}
            <span className="text-sm font-medium">Credential Drift Monitor</span>
            {driftStatus?.alerts?.new > 0 && (
              <Badge variant="outline" className="text-red-600 dark:text-red-400 border-red-500/30 text-[10px]">
                {driftStatus.alerts.new} alert{driftStatus.alerts.new > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {driftStatus?.last_check && (
              <span className="text-[10px] text-muted-foreground">
                Last: {new Date(driftStatus.last_check).toLocaleString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={triggerDriftCheck}
              disabled={checkingDrift}
              data-testid="drift-check-now-btn"
            >
              {checkingDrift ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Shield className="w-3 h-3 mr-1" />
              )}
              Check Now
            </Button>
          </div>
        </div>
        {driftAlerts.length > 0 ? (
          <div className="space-y-2">
            {driftAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`flex items-start justify-between gap-2 p-2.5 rounded-lg border ${
                  alert.severity === "critical"
                    ? "bg-red-500/5 border-red-500/20"
                    : alert.severity === "high"
                    ? "bg-amber-500/5 border-amber-500/20"
                    : "bg-yellow-500/5 border-yellow-500/20"
                }`}
                data-testid={`drift-alert-${alert.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge
                      className={`text-[10px] ${
                        alert.severity === "critical"
                          ? "bg-red-500/20 text-red-600 dark:text-red-400"
                          : alert.severity === "high"
                          ? "bg-amber-500/20 text-amber-600 dark:text-amber-400"
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}
                    >
                      {alert.severity}
                    </Badge>
                    <span className="text-xs font-medium">{alert.credential_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{alert.detail}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] shrink-0"
                  onClick={() => acknowledgeDriftAlert(alert.id)}
                >
                  Dismiss
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">All credentials healthy — no drift detected</p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {statCards.map((card) => (
          <Card
            key={card.label}
            className="bg-[var(--n-card)] border-[var(--n-border-2)] hover:border-[#2a2a30] transition-colors"
            data-testid={`stat-card-${card.label.toLowerCase().replace(/[\s\/]/g, "-")}`}
          >
            <CardContent className="p-4 lg:p-6">
              <div className={`p-2 rounded-lg ${card.bg} w-fit`}>
                <card.icon className={`w-4 h-4 ${card.accent}`} />
              </div>
              <div className="mt-3">
                <p className="text-2xl lg:text-3xl font-bold font-mono">
                  {card.value}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {card.label}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <Card className="lg:col-span-2 bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Layers className="w-4 h-4 text-[var(--hig-accent)]" />
              Resource Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.type_breakdown?.length > 0 ? (
              <div className="space-y-3">
                {stats.type_breakdown.map((t, i) => {
                  const maxCount = stats.type_breakdown[0]?.count || 1;
                  const pct = (t.count / maxCount) * 100;
                  const shortType = t.type?.split("/").pop() || t.type;
                  return (
                    <div key={i} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span
                          className="text-muted-foreground truncate max-w-[70%]"
                          title={t.type}
                        >
                          {shortType}
                        </span>
                        <span className="font-mono text-foreground">
                          {t.count}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[var(--n-border-2)] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[var(--hig-accent)] rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Database className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No resources discovered yet
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Configure Azure credentials and run a scan
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-[var(--hig-accent)]/20 text-[var(--hig-accent)] hover:bg-[var(--hig-accent)]/10"
                  onClick={() => navigate("/settings")}
                  data-testid="go-to-settings-btn"
                >
                  <Settings className="w-3 h-3 mr-2" /> Configure
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                Recent Events
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-[var(--hig-accent)]"
                onClick={() => navigate("/feed")}
                data-testid="view-all-events-btn"
              >
                View all <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_events?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_events.slice(0, 6).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-start gap-3 py-2 border-b border-[var(--n-border-2)] last:border-0"
                  >
                    <div className="mt-0.5">
                      {event.event_type === "created" && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      )}
                      {event.event_type === "modified" && (
                        <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      )}
                      {event.event_type === "deleted" && (
                        <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-foreground truncate">
                        {event.resource_name}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] shrink-0 ${eventTypeColors[event.event_type] || ""}`}
                    >
                      {event.event_type}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No events yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Events appear after scanning
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscription Breakdown */}
      {stats?.subscription_breakdown?.length > 1 && !subscriptionId && (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Globe className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              Subscription Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.subscription_breakdown.map((sub) => (
                <button
                  key={sub.subscription_id}
                  onClick={() => setSubscriptionId(sub.subscription_id)}
                  className="text-left p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] hover:border-violet-500/30 transition-colors"
                  data-testid={`sub-card-${sub.subscription_id}`}
                >
                  <p className="text-sm font-medium truncate">{sub.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate">
                    {sub.subscription_id}
                  </p>
                  <p className="text-lg font-bold font-mono mt-2 text-violet-600 dark:text-violet-400">
                    {sub.count}
                    <span className="text-xs text-muted-foreground font-normal ml-1">
                      resources
                    </span>
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.total_credentials === 0 && (
        <Card className="bg-[var(--n-card)] border-[var(--hig-accent)]/20">
          <CardContent className="p-4 lg:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="p-3 rounded-xl bg-[var(--hig-accent)]/10">
              <Radar className="w-6 h-6 text-[var(--hig-accent)]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">Get Started</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add your Azure Service Principal credentials to start
                discovering resources
              </p>
            </div>
            <Button
              onClick={() => navigate("/settings")}
              className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
              data-testid="setup-credentials-btn"
            >
              Setup Credentials
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
